/**
 * Passkey (WebAuthn) helper utilities.
 *
 * Wraps @simplewebauthn/browser for the staff portal. Handles the round-trip
 * with the FastAPI backend (`/auth/passkeys/*`) and surfaces friendly error
 * messages for unsupported browsers / user cancellation.
 */
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import { api } from "@/lib/api";

const PASSKEY_PROMPT_DISMISS_KEY = "clc_passkey_prompt_dismissed_at";
const PASSKEY_PROMPT_COOLDOWN_DAYS = 30;

export const passkeysSupported = () => browserSupportsWebAuthn();

export const platformAuthenticatorAvailable = async () => {
  if (!browserSupportsWebAuthn()) return false;
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
};

/** Returns a friendly device label like "iPhone · Safari" for the new passkey. */
export const guessDeviceLabel = () => {
  if (typeof navigator === "undefined") return "Passkey";
  const ua = navigator.userAgent || "";
  let device = "Device";
  if (/iPhone/i.test(ua)) device = "iPhone";
  else if (/iPad/i.test(ua)) device = "iPad";
  else if (/Android/i.test(ua)) device = "Android";
  else if (/Macintosh|Mac OS X/i.test(ua)) device = "Mac";
  else if (/Windows/i.test(ua)) device = "Windows";
  else if (/Linux/i.test(ua)) device = "Linux";
  let browser = "";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/Chrome\//i.test(ua)) browser = "Chrome";
  else if (/Firefox\//i.test(ua)) browser = "Firefox";
  else if (/Safari\//i.test(ua)) browser = "Safari";
  return browser ? `${device} · ${browser}` : device;
};

/** Register a new passkey for the currently signed-in user. */
export async function registerPasskey({ deviceLabel } = {}) {
  if (!browserSupportsWebAuthn()) {
    throw new Error("This browser does not support passkeys.");
  }
  const label = deviceLabel || guessDeviceLabel();
  const { data: options } = await api.post(
    "/auth/passkeys/registration/options",
    { device_label: label },
  );
  let credential;
  try {
    credential = await startRegistration(options);
  } catch (err) {
    const mapped = friendlyPasskeyError(err);
    const wrapped = new Error(
      mapped.cancelled ? "Passkey setup cancelled." : mapped.message
    );
    wrapped.cancelled = !!mapped.cancelled;
    throw wrapped;
  }
  const { data } = await api.post("/auth/passkeys/registration/verify", {
    credential,
    device_label: label,
  });
  return data;
}

/** Translate browser/platform passkey errors into friendly user messages. */
function friendlyPasskeyError(err) {
  const name = err?.name || "";
  const msg = err?.message || String(err || "");
  // User cancelled the OS prompt (any platform) — silent dismiss.
  if (name === "NotAllowedError" || /cancel/i.test(msg)) {
    return { cancelled: true, message: "Passkey sign-in cancelled." };
  }
  // Android Credential Manager: "An unknown error occurred while talking to
  // the credential manager." — almost always means no passkey is saved on
  // this device for this site yet, or the device's screen lock isn't set.
  if (/credential manager|UnknownError|no.*passkey|no credentials? available/i.test(msg)) {
    return {
      message:
        "No passkey is saved on this device yet. Sign in with your PIN, then turn on Face/Fingerprint sign-in from the menu (Account → Manage passkeys).",
    };
  }
  if (name === "SecurityError" || /security/i.test(msg)) {
    return {
      message:
        "Your browser blocked the passkey request. On Android, make sure the screen lock is set and Google Password Manager is enabled.",
    };
  }
  if (name === "InvalidStateError") {
    return {
      message: "This passkey is already registered on your account.",
    };
  }
  return { message: msg || "Passkey sign-in failed." };
}

/** Sign in with a previously-registered passkey. Returns the user object. */
export async function authenticateWithPasskey() {
  if (!browserSupportsWebAuthn()) {
    throw new Error("This browser does not support passkeys.");
  }
  const { data: options } = await api.post(
    "/auth/passkeys/authentication/options",
    {},
  );
  let assertion;
  try {
    assertion = await startAuthentication(options);
  } catch (err) {
    const mapped = friendlyPasskeyError(err);
    const wrapped = new Error(mapped.message);
    wrapped.cancelled = !!mapped.cancelled;
    throw wrapped;
  }
  const { data } = await api.post("/auth/passkeys/authentication/verify", {
    credential: assertion,
  });
  return data.user;
}

export async function listMyPasskeys() {
  const { data } = await api.get("/auth/passkeys/me");
  return data.passkeys || [];
}

export async function revokeMyPasskey(credId) {
  await api.delete(`/auth/passkeys/me/${credId}`);
}

/**
 * Returns true when we should show the "Save passkey" prompt to the user.
 * Dismissals are cooldowned for 30 days, and we only prompt once we know
 * a platform authenticator is actually available on the device.
 */
export async function shouldPromptSavePasskey() {
  if (!(await platformAuthenticatorAvailable())) return false;
  if (typeof window === "undefined") return false;
  const dismissed = window.localStorage.getItem(PASSKEY_PROMPT_DISMISS_KEY);
  if (!dismissed) return true;
  const ts = parseInt(dismissed, 10);
  if (!Number.isFinite(ts)) return true;
  const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  return ageDays > PASSKEY_PROMPT_COOLDOWN_DAYS;
}

export function dismissPasskeyPrompt() {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PASSKEY_PROMPT_DISMISS_KEY, String(Date.now()));
}

/** Marker that the user already has at least one passkey — show the
 * sign-in shortcut on the login page without round-tripping. */
const HAS_PASSKEY_KEY = "clc_passkey_registered";
export function markPasskeyRegistered() {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(HAS_PASSKEY_KEY, "1");
}
export function hasLocalPasskeyHint() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(HAS_PASSKEY_KEY) === "1";
}
export function clearLocalPasskeyHint() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(HAS_PASSKEY_KEY);
}
